import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { formatDimensionsCompact } from '../../../lib/parseTyreDimensions';
import EULabelBadge from '../EULabelBadge';
import {
  TyreName,
  TyreDesign,
  Dimensions,
  SpecsGrid,
  SpecItem,
  SpecValue,
  SeasonBadge,
} from './TyreInfo.style';

const SEASON_MAP = {
  summer: 'summer',
  winter: 'winter',
  'all season': 'allSeason',
  'all-season': 'allSeason',
};

const isValidSeason = (seasonName) => {
  return seasonName && SEASON_MAP[seasonName.toLowerCase()] != null;
};

const getSeasonTranslationKey = (seasonName) => {
  return SEASON_MAP[seasonName?.toLowerCase()] || null;
};

const getSeasonIcon = (season) => {
  if (!season?.name) return 'mdi-weather-cloudy';
  const seasonName = season.name.toLowerCase();
  if (seasonName === 'summer') return 'mdi-white-balance-sunny';
  if (seasonName === 'winter') return 'mdi-snowflake';
  if (seasonName === 'all-season') return 'mdi-weather-partly-cloudy';
  return 'mdi-weather-cloudy';
};

const TyreInfo = ({ tyre }) => {
  const { t } = useTranslation();

  return (
    <>
      <TyreName>{tyre.name}</TyreName>
      {tyre.design && <TyreDesign>{tyre.design}</TyreDesign>}

      {tyre.dimensions && !/^0+$/.test(tyre.dimensions.trim()) && (
        <Dimensions>
          {formatDimensionsCompact(tyre.dimensions) || tyre.dimensions}
        </Dimensions>
      )}

      {tyre.season && isValidSeason(tyre.season.name) && (
        <SeasonBadge>
          <i className={`mdi ${getSeasonIcon(tyre.season)}`} />
          {t(getSeasonTranslationKey(tyre.season.name))}
        </SeasonBadge>
      )}

      <SpecsGrid>
        <SpecItem>
          <strong>{t('catalog')}:</strong>
          <SpecValue>{tyre.catalogNumber}</SpecValue>
        </SpecItem>
        {tyre.ean && (
          <SpecItem>
            <strong>EAN:</strong>
            <SpecValue>{tyre.ean}</SpecValue>
          </SpecItem>
        )}
      </SpecsGrid>

      <EULabelBadge
        rollingResistance={tyre.rollingResistance}
        wetPerformance={tyre.wetPerformance}
        noiseLevel={tyre.noiseLevel}
        noiseType={tyre.noiseType}
      />
    </>
  );
};

TyreInfo.propTypes = {
  tyre: PropTypes.shape({
    name: PropTypes.string,
    design: PropTypes.string,
    dimensions: PropTypes.string,
    catalogNumber: PropTypes.string,
    ean: PropTypes.string,
    season: PropTypes.shape({ name: PropTypes.string }),
    rollingResistance: PropTypes.string,
    wetPerformance: PropTypes.string,
    noiseLevel: PropTypes.number,
    noiseType: PropTypes.string,
  }).isRequired,
};

export default TyreInfo;
